import { ReactNode } from "react";
import { CheckCircle, Star, Users, Zap } from "lucide-react";
import { Agent } from "@/types/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

interface SalesTemplateProps {
  agent: Agent;
  children: ReactNode;
}

const SalesTemplate = ({ agent, children }: SalesTemplateProps) => {
  const brand =
    agent?.agents?.branding?.primaryColor ||
    agent.branding?.primaryColor ||
    agent.brand_color ||
    DEFAULT_BRANDING.primaryColor;
  const secondaryColor =
    agent?.agents?.branding?.secondaryColor || agent.branding?.secondaryColor || DEFAULT_BRANDING.secondaryColor;

  return (
    <div style={{ minHeight: "100vh", width: "100%", background: "#F9FAFC" }}>
      {/* Header Section */}
      <div
        style={{
          background: `linear-gradient(135deg, ${brand} 0%, ${secondaryColor} 100%)`,
          padding: "60px 20px 120px",
          textAlign: "center",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 3rem)",
              fontWeight: "bold",
              color: "#ffffff",
              marginBottom: "1rem",
              lineHeight: "1.2",
            }}
          >
            Talk to Our Sales Expert
          </h1>
          <p
            style={{
              fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
              color: "rgba(255, 255, 255, 0.95)",
              marginBottom: "2rem",
              lineHeight: "1.6",
            }}
          >
            Get personalized recommendations, pricing information, and book a demo in minutes
          </p>

          {/* Trust Indicators */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "2rem",
              marginTop: "2rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "rgba(255, 255, 255, 0.9)" }}>
              <CheckCircle size={20} />
              <span style={{ fontSize: "0.95rem" }}>Free Consultation</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "rgba(255, 255, 255, 0.9)" }}>
              <Zap size={20} />
              <span style={{ fontSize: "0.95rem" }}>Instant Response</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "rgba(255, 255, 255, 0.9)" }}>
              <Users size={20} />
              <span style={{ fontSize: "0.95rem" }}>Trusted by 1000+</span>
            </div>
          </div>
        </div>
      </div>

      {/* Agent Conversation Card */}
      <div
        style={{
          maxWidth: "900px",
          margin: "-80px auto 0",
          padding: "0 20px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            padding: "60px 40px",
            border: `2px solid ${brand}20`,
          }}
        >
          {children}
        </div>
      </div>

      {/* Benefits Section */}
      <div style={{ maxWidth: "1000px", margin: "80px auto", padding: "0 20px" }}>
        <h2
          style={{
            fontSize: "clamp(1.5rem, 3vw, 2rem)",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "3rem",
            color: "#1F2937",
          }}
        >
          Why Talk to Us?
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "2rem",
          }}
        >
          {[
            {
              icon: "🎯",
              title: "Personalized Solutions",
              description: "Get tailored recommendations based on your specific needs and insights",
            },
            {
              icon: "⚡",
              title: "Fast Response",
              description: "No waiting in queues. Get instant answers to all your questions",
            },
            {
              icon: "💎",
              title: "Expert Guidance",
              description: "Access professional insights and industry best practices",
            },
          ].map((benefit, idx) => (
            <div
              key={idx}
              style={{
                background: "#ffffff",
                padding: "2rem",
                borderRadius: "16px",
                textAlign: "center",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                border: "1px solid #E5E7EB",
                transition: "transform 0.2s ease",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{benefit.icon}</div>
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  marginBottom: "0.75rem",
                  color: brand,
                }}
              >
                {benefit.title}
              </h3>
              <p style={{ color: "#6B7280", fontSize: "0.95rem", lineHeight: "1.6" }}>{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div
        style={{
          background: `linear-gradient(135deg, ${brand}10 0%, ${secondaryColor}10 100%)`,
          padding: "60px 20px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginBottom: "1rem" }}>
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={24} fill={brand} color={brand} />
            ))}
          </div>
          <p style={{ fontSize: "1.1rem", color: "#374151", fontStyle: "italic", marginBottom: "1rem" }}>
            "Incredibly helpful and responsive. Got all the information I needed in just a few minutes!"
          </p>
          <p style={{ fontSize: "0.9rem", color: "#6B7280" }}>— Satisfied Customer</p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          color: "#6B7280",
          fontSize: "0.9rem",
        }}
      >
        <p>Secure • Confidential • Available 24/7</p>
      </div>
    </div>
  );
};

export default SalesTemplate;
